import React from 'react';
import styled, { keyframes } from 'styled-components';
import Layout from '../components/Layout';
import Container from '../components/Container';
import Footer from '../components/Footer';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PageWrapper = styled.div`
  padding: 7rem 0 4rem;
  background: linear-gradient(135deg, #0e1633 0%, #1e2a4a 100%);
  min-height: 80vh;
  
  @media (max-width: 768px) {
    padding: 5rem 0 3rem;
  }
  
  @media (max-width: 480px) {
    padding: 4rem 0 2rem;
  }
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  text-align: center;
  margin-bottom: 3rem;
  color: #fff;
  text-shadow: 0 0 16px #2d6cff, 0 2px 10px #0008;
  animation: ${fadeIn} 0.8s ease-out;
  
  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.8rem;
    margin-bottom: 1.5rem;
  }
`;

const FAQContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  animation: ${fadeIn} 1s ease-out;
  
  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const FAQItem = styled.div`
  margin-bottom: 2rem;
  background: rgba(20, 30, 60, 0.85);
  border-radius: 16px;
  box-shadow: 0 8px 32px 0 #2d6cff33;
  border: 1.5px solid #2d6cff44;
  overflow: hidden;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 0 32px #2d6cffcc;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 1.2rem;
    &:hover {
      transform: translateY(-3px);
    }
  }
`;

const Question = styled.div`
  padding: 1.5rem;
  font-weight: 600;
  font-size: 1.1rem;
  color: #fff;
  background: linear-gradient(90deg, #233a7c 0%, #2d6cff33 100%);
  border-left: 4px solid #2d6cff;
  text-shadow: 0 0 8px #2d6cffcc;
  
  @media (max-width: 768px) {
    padding: 1.2rem;
    font-size: 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
    font-size: 0.95rem;
    line-height: 1.4;
  }
`;

const Answer = styled.div`
  padding: 1.5rem;
  color: #b6d0ff;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    padding: 1.2rem;
    line-height: 1.5;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
    font-size: 0.95rem;
  }
  
  p {
    margin-bottom: 1rem;
    
    &:last-child {
      margin-bottom: 0;
    }
    
    @media (max-width: 480px) {
      margin-bottom: 0.8rem;
    }
  }
  
  a {
    color: #2d6cff;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
  
  ul, ol {
    margin-left: 1.5rem;
    margin-bottom: 1rem;
    
    @media (max-width: 480px) {
      margin-left: 1.2rem;
      margin-bottom: 0.8rem;
    }
    
    li {
      margin-bottom: 0.5rem;
      
      @media (max-width: 480px) {
        margin-bottom: 0.4rem;
      }
    }
  }
`;

function FAQPage() {
  return (
    <Layout>
      <PageWrapper>
        <Container>
          <PageTitle>Frequently Asked Questions</PageTitle>
          
          <FAQContainer>
            <FAQItem>
              <Question>Do I need an AdsPower subscription to use AccountWarmer?</Question>
              <Answer>
                <p><strong>Yes</strong>, AccountWarmer utilizes the AdsPower Local API to function properly. You'll need a valid AdsPower subscription, which typically costs between $5-20 per month depending on the plan you choose.</p>
                <p>If you contact us directly via our Telegram support (<a href="https://t.me/deserved0" target="_blank" rel="noopener noreferrer">@deserved0</a>), we may be able to help you get a discounted rate on AdsPower subscriptions.</p>
              </Answer>
            </FAQItem>
            
            <FAQItem>
              <Question>What is AdsPower and why do I need it?</Question>
              <Answer>
                <p>AdsPower is a browser profile management tool that helps create and manage multiple isolated browser environments. AccountWarmer leverages AdsPower's capabilities to:</p>
                <ul>
                  <li>Create separate browser profiles for each platform (Amazon, eBay, Vinted)</li>
                  <li>Manage fingerprints to make each profile appear unique</li>
                  <li>Automate browser interactions that warm up your profiles</li>
                  <li>Keep cookies, history, and browsing data separate for each profile</li>
                </ul>
                <p>Without AdsPower, our application cannot properly isolate and manage profiles, which is essential for successful account warming.</p>
              </Answer>
            </FAQItem>
            
            <FAQItem>
              <Question>How does the AccountWarmer software work?</Question>
              <Answer>
                <p>AccountWarmer automates the process of warming up browser profiles through these steps:</p>
                <ol>
                  <li>Creates and manages isolated browser profiles using AdsPower</li>
                  <li>Executes natural browsing patterns on target platforms (Amazon, eBay, Vinted)</li>
                  <li>Simulates human-like activity including product browsing, searches, and page interactions</li>
                  <li>Gradually builds browsing history and platform-specific patterns</li>
                  <li>Helps prevent suspicious activity flags during account creation</li>
                </ol>
                <p>The software is designed to mimic natural human behavior to help your new accounts appear legitimate to platform algorithms.</p>
              </Answer>
            </FAQItem>
            
            <FAQItem>
              <Question>What are the system requirements?</Question>
              <Answer>
                <p>To run AccountWarmer effectively, you'll need:</p>
                <ul>
                  <li>Windows 10/11 or macOS 10.15 (Catalina) or newer</li>
                  <li>8GB RAM minimum (16GB recommended)</li>
                  <li>AdsPower software installed and configured</li>
                  <li>Stable internet connection</li>
                  <li>Recommended: Residential or high-quality proxy (not strictly required but highly recommended)</li>
                </ul>
              </Answer>
            </FAQItem>
            
            <FAQItem>
              <Question>Do you offer refunds?</Question>
              <Answer>
                <p>Due to the digital nature of our product, we generally do not offer refunds once the software license key has been issued. However, we're committed to customer satisfaction:</p>
                <ul>
                  <li>If you encounter technical issues, our support team will help troubleshoot</li>
                  <li>In case of major software malfunctions that we cannot resolve, we may offer partial refunds or extended subscription periods</li>
                </ul>
                <p>Please contact our support team with any concerns about your purchase.</p>
              </Answer>
            </FAQItem>
            
            <FAQItem>
              <Question>How many accounts can I warm simultaneously?</Question>
              <Answer>
                <p>The number of accounts you can warm simultaneously depends on:</p>
                <ol>
                  <li>Your AdsPower subscription tier (which limits the number of browser profiles)</li>
                  <li>Your computer's resources (RAM, CPU)</li>
                  <li>Your internet connection quality</li>
                </ol>
                <p>Most users can comfortably run 3-5 profile warming sessions simultaneously on a mid-range computer. For larger scale operations, you may need more powerful hardware or multiple machines.</p>
              </Answer>
            </FAQItem>
          </FAQContainer>
          
        </Container>
      </PageWrapper>
      <Footer />
    </Layout>
  );
}

export default FAQPage; 